import { useState, useRef, useEffect } from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "../utils/cn";

type OpsFamilyComboboxProps = {
  value: string | null;
  onChange: (value: string | null) => void;
  suggestedValues: string[];
  placeholder?: string;
  disabled?: boolean;
  "aria-label"?: string;
};

/**
 * Single-value combobox: click to focus, filter list as you type,
 * click or Enter to select; Enter on new value sets it.
 */
export function OpsFamilyCombobox({
  value,
  onChange,
  suggestedValues,
  placeholder = "Famille opérationnelle…",
  disabled = false,
  "aria-label": ariaLabel = "Famille opérationnelle",
}: OpsFamilyComboboxProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState(value ?? "");
  const [highlightIndex, setHighlightIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const filteredSuggestions = suggestedValues.filter(
    (s) =>
      s &&
      (!inputValue.trim() || s.toLowerCase().includes(inputValue.trim().toLowerCase()))
  );
  const exactMatch = inputValue.trim() && filteredSuggestions.some((s) => s === inputValue.trim());
  const canSetNew = inputValue.trim();

  useEffect(() => {
    setInputValue(value ?? "");
  }, [value]);

  useEffect(() => {
    if (!isOpen) return;
    setHighlightIndex(0);
  }, [inputValue, isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const onMouseDown = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        applyAndClose();
      }
    };
    document.addEventListener("mousedown", onMouseDown);
    return () => document.removeEventListener("mousedown", onMouseDown);
  }, [isOpen]);

  const applyAndClose = () => {
    const v = inputValue.trim() || null;
    if (v !== (value ?? "")) onChange(v);
    setInputValue(value ?? "");
    setIsOpen(false);
    inputRef.current?.blur();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const selected = filteredSuggestions[highlightIndex];
      const toSet = selected ?? (canSetNew ? inputValue.trim() : null);
      if (toSet !== undefined) {
        onChange(toSet || null);
        setInputValue(toSet ?? "");
      }
      setIsOpen(false);
      inputRef.current?.blur();
      return;
    }
    if (e.key === "Escape") {
      setInputValue(value ?? "");
      setIsOpen(false);
      inputRef.current?.blur();
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightIndex((i) => Math.min(i + 1, filteredSuggestions.length));
      return;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightIndex((i) => Math.max(i - 1, 0));
    }
  };

  return (
    <div ref={containerRef} className="space-y-1">
      <div
        className={cn(
          "min-h-9 rounded-md border border-border bg-surface px-2 py-1.5 flex items-center gap-1",
          isOpen && "ring-2 ring-primary/30 border-primary/50",
          disabled && "opacity-60 pointer-events-none"
        )}
        onClick={() => !disabled && (setIsOpen(true), setTimeout(() => inputRef.current?.focus(), 0))}
      >
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => setIsOpen(true)}
          placeholder={placeholder}
          aria-label={ariaLabel}
          className="flex-1 min-w-0 bg-transparent border-none outline-none text-sm text-text placeholder:text-muted py-0.5"
        />
        <ChevronDown size={16} className="text-muted shrink-0" />
      </div>
      {isOpen && (filteredSuggestions.length > 0 || canSetNew) && (
        <div
          className="rounded-md border border-border bg-surface shadow-lg py-1 max-h-48 overflow-y-auto z-10"
          role="listbox"
        >
          {filteredSuggestions.map((s, i) => (
            <button
              key={s}
              type="button"
              role="option"
              aria-selected={i === highlightIndex}
              className={cn(
                "w-full text-left px-3 py-2 text-sm text-text hover:bg-surface-muted",
                i === highlightIndex && "bg-surface-muted"
              )}
              onMouseEnter={() => setHighlightIndex(i)}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onChange(s);
                setInputValue(s);
                setIsOpen(false);
              }}
            >
              {s}
            </button>
          ))}
          {canSetNew && !exactMatch && (
            <button
              type="button"
              role="option"
              aria-selected={filteredSuggestions.length === highlightIndex}
              className={cn(
                "w-full text-left px-3 py-2 text-sm text-primary hover:bg-surface-muted",
                filteredSuggestions.length === highlightIndex && "bg-surface-muted"
              )}
              onMouseEnter={() => setHighlightIndex(filteredSuggestions.length)}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onChange(inputValue.trim());
                setInputValue(inputValue.trim());
                setIsOpen(false);
              }}
            >
              + Utiliser « {inputValue.trim()} »
            </button>
          )}
        </div>
      )}
    </div>
  );
}
